import { Fragment, useState } from 'react'
import { Dialog, useCopyToClipboard } from '@region13/ui'
import { formatPhone, phoneHref } from '@region13/shared-types'
import type { Volunteer } from '../lib/api'

/** What a person is called on Home: their name, else their address. */
function displayName(p: Volunteer): string {
  return p.name ?? p.email ?? 'Unnamed volunteer'
}

/** A touch screen can place a call; a mouse cannot. The pointer, not the user
 *  agent, decides, so a touch laptop driven by a mouse counts as desktop. */
function canDial(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
}

/** The phone, formatted. On a touch device it dials (`tel:`), or is plain text
 *  when the stored value is not safely dialable; on desktop a click copies it,
 *  with the shared fallback box where the clipboard is unavailable. */
function PhoneValue({ raw }: { raw: string }) {
  const shown = formatPhone(raw)
  const [dial] = useState(canDial)
  const { state, copy, boxRef, buttonRef, close, dismiss } = useCopyToClipboard()

  if (dial) {
    const href = phoneHref(raw)
    return href ? <a className="underline" href={href}>{shown}</a> : <>{shown}</>
  }
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="underline hover:text-brand-maroon"
        aria-label={`Copy phone ${shown}`}
        title="Copy phone number"
        onClick={() => void copy(shown)}
      >
        {shown}
      </button>
      <span role="status" aria-live="polite" className="ml-2 text-ink-soft empty:ml-0">{state === 'copied' ? 'Copied' : ''}</span>
      {state === 'manual' && (
        <input
          ref={boxRef}
          readOnly
          autoFocus
          value={shown}
          aria-label="Phone to copy"
          className="mt-1 block w-40 rounded border border-line bg-surface px-2 py-1 text-sm text-ink"
          onBlur={close}
          onKeyDown={(e) => {
            // Stop here, or the dialog's own Escape handler closes the dialog too.
            if (e.key === 'Escape') { e.stopPropagation(); dismiss() }
          }}
        />
      )}
    </>
  )
}

/** One team volunteer's details. Renders only data Home already loaded: no
 *  request on open. The phone is InLeague's `primaryPhone`, labeled Phone
 *  because InLeague does not say it is a mobile. Email is a `mailto:` link
 *  everywhere. */
function VolunteerModal({ person, onClose }: { person: Volunteer | null; onClose: () => void }) {
  return (
    <Dialog open={person !== null} onClose={onClose} title={person ? displayName(person) : ''}>
      {person && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-5 py-4 text-sm">
          <dt className="text-ink-soft">Role</dt>
          <dd>{person.roles.join(', ')}</dd>
          <dt className="text-ink-soft">Email</dt>
          <dd>{person.email ? <a className="underline" href={`mailto:${person.email}`}>{person.email}</a> : 'Not on file'}</dd>
          <dt className="text-ink-soft">Phone</dt>
          <dd>{person.phone ? <PhoneValue raw={person.phone} /> : 'Not on file'}</dd>
        </dl>
      )}
    </Dialog>
  )
}

/** A comma-separated row of volunteer names, each opening that person's
 *  details. `empty` shows when nobody is assigned. */
export function VolunteerList({ people, empty }: { people: Volunteer[]; empty: string }) {
  const [open, setOpen] = useState<Volunteer | null>(null)
  if (people.length === 0) return <>{empty}</>
  return (
    <>
      {people.map((p, i) => (
        <Fragment key={`${p.email ?? p.name ?? ''}-${i}`}>
          {i > 0 && ', '}
          <button type="button" className="underline hover:text-brand-maroon" onClick={() => setOpen(p)}>
            {displayName(p)}
          </button>
        </Fragment>
      ))}
      <VolunteerModal person={open} onClose={() => setOpen(null)} />
    </>
  )
}
